/**
 * A community's courses in `wayai pull` / `wayai push communities/<x>` (gh #5003): the course
 * folders (`course-files.ts`) synced three-way per entity (`model.ts`, the Sync rules table); each
 * parent's order — the community's courses, a course's modules, a module's lessons — three-way
 * against the list of its children's ids the files keep as of the last sync (`synced:`); and each
 * lesson's place among its course's modules, against the module whose synced list holds it.
 *
 * READ: the courses, drafts included, then each one whole in one request (its authoring export).
 *
 * PUSH applies, in order: unpublish (courses, then lessons) → [settings, the join link and rooms:
 * `sync.ts`] → courses (created as drafts) → modules → lessons → places that change → publish
 * courses last, so a course goes live only once its lessons are in place. A new lesson is created
 * in one request with all its fields, placed last. Each create sends a `client_id` derived from
 * its stable local name (`createClientId`): a retried create, or one whose id the files lost,
 * answers the entity made before, which push then checks against the files — one of another course
 * stops the push, any other difference is written in the same run.
 *
 * Every write records what it changed in the files as soon as it returns: the entity's id and
 * rev, and each parent's synced list, computed from the server's deterministic placement (`Sim`,
 * which applies each create and position write as the server does). So a push stopped anywhere
 * leaves every base true to the server, and its rerun converges.
 *
 * PULL takes what changed on the server per entity and per order, keeps what changed in the files
 * only, and writes the courses, modules and lessons the files do not have yet.
 */

import {
  COMMUNITY_COURSES_MAX,
  COMMUNITY_COURSE_LESSONS_MAX,
  COMMUNITY_COURSE_MODULES_MAX,
  COMMUNITY_LESSONS_MAX,
  COMMUNITY_LESSON_LINE_PREFIX_TOO_WIDE_REASON,
  COMMUNITY_LESSON_TREE_TOO_DEEP_REASON,
  COMMUNITY_LESSON_TREE_TOO_LARGE_REASON,
  communityLessonVideoWatchUrl,
  parseCommunityLessonVideoUrl,
  type CommunityCourseCreateBody,
  type CommunityCourseDraft,
  type CommunityCourseExport,
  type CommunityCourseUpdateBody,
  type CommunityLessonCreateBody,
  type CommunityLessonDraft,
  type CommunityLessonUpdateBody,
  type CommunityModuleCreateBody,
  type CommunityModuleDraft,
  type CommunityModuleUpdateBody,
} from '@wayai/core/contracts';
import { isDeepStrictEqual } from 'node:util';
import * as path from 'node:path';
import { COMMUNITY_FILE } from '../lib/community-workspace.js';
import { extractApiErrorReason, extractApiMessage } from '../lib/errors.js';
import { expected } from '../lib/expected.js';
import { LESSON_FILE_EXT, lessonPath, type CourseFolder, type CourseSynced, type LessonFile, type ModuleEntry } from './course-files.js';
import type { CommunityFiles, RoomEntry } from './files.js';
import { lineDiff, show, type Listing } from './listing.js';
import {
  classify,
  courseContent,
  courseRev,
  createClientId,
  lessonContent,
  lessonRev,
  localName,
  moduleContent,
  moduleRev,
  serverCourse,
  serverLesson,
  serverModule,
  threeWay,
  toLf,
  type CourseContent,
  type LessonContent,
  type ServerLesson,
  type SyncState,
} from './model.js';
import { filesOrder, idsOf, mergeOrder, placeAt, reorderMoves, restrict, sameOrder, type Listed } from './order.js';

type ServerModule = CommunityCourseExport['modules'][number];
type ExportLesson = ServerModule['lessons'][number];

/** One write of a push: what it is, the requests it makes, and the write itself. */
export interface Write {
  label: string;
  requests: number;
  apply: () => Promise<void>;
}

// ── The server's courses ──

/** The courses as read, in order, each whole; and where each module and lesson is. */
export class ServerCourses {
  readonly course = new Map<string, CommunityCourseExport>();
  readonly module = new Map<string, { courseId: string; module: ServerModule }>();
  readonly lesson = new Map<string, { courseId: string; moduleId: string; lesson: ExportLesson }>();

  constructor(readonly courses: CommunityCourseExport[]) {
    for (const course of courses) {
      const courseId = course.course.course_id;
      this.course.set(courseId, course);
      for (const module of course.modules) {
        this.module.set(module.module_id, { courseId, module });
        for (const lesson of module.lessons) this.lesson.set(lesson.lesson_id, { courseId, moduleId: module.module_id, lesson });
      }
    }
  }

  get ids(): string[] {
    return this.courses.map((course) => course.course.course_id);
  }
}

// ── The files' content, normalized ──

/** What a lesson's room id is taken to be while its room is one this push creates first. */
const PENDING_ROOM = 'pending:';

/** A room key's id: the room's, or a stand-in for a room the files create; null for none. */
function roomIdOf(rooms: RoomEntry[], key: string | undefined): string | null {
  if (key === undefined) return null;
  const room = rooms.find((entry) => entry.key === key);
  return room?.id ?? `${PENDING_ROOM}${key}`;
}

function fileLesson(rooms: RoomEntry[], lesson: LessonFile): LessonContent {
  return lessonContent({
    title: lesson.title,
    body: lesson.body,
    video: lesson.video_url === undefined ? null : parseCommunityLessonVideoUrl(lesson.video_url),
    links: lesson.links,
    room_id: roomIdOf(rooms, lesson.room),
    requires_paid_plan: lesson.requires_paid_plan,
    published: lesson.published,
  });
}


/** A room id as printed: its key, or a stand-in's key; 'none' for none. */
function roomName(rooms: RoomEntry[], id: string | null): string {
  if (id === null) return 'none';
  if (id.startsWith(PENDING_ROOM)) return id.slice(PENDING_ROOM.length);
  return rooms.find((room) => room.id === id)?.key ?? id;
}

/** A room id's key in `community.yaml`; none for none. */
function roomKeyOf(rooms: RoomEntry[], roomId: string | null): string | undefined {
  if (roomId === null) return undefined;
  const room = rooms.find((entry) => entry.id === roomId);
  if (!room) throw expected(`room ${roomId} is not one of the rooms ${COMMUNITY_FILE} lists: pull the community first.`);
  return room.key;
}

/**
 * The server's lesson into a lesson file, field by field — `fields` (a write's, by the names its
 * body gives them), or all of them — where it reads otherwise than the file: the file keeps its own
 * form (a URL, spacing) where the two read alike.
 */
function takeLesson(rooms: RoomEntry[], lesson: LessonFile, server: ServerLesson, fields?: ReadonlySet<string>): void {
  const file = fileLesson(rooms, lesson);
  const side = lessonContent(server);
  const take = (field: string, differs: boolean) => differs && (fields === undefined || fields.has(field));
  if (take('title', file.title !== side.title)) lesson.title = server.title;
  if (take('body', file.body !== side.body)) lesson.body = side.body;
  if (take('video_url', !isDeepStrictEqual(file.video, side.video))) lesson.video_url = side.video ? communityLessonVideoWatchUrl(side.video) : undefined;
  if (take('links', !isDeepStrictEqual(file.links, side.links))) lesson.links = server.links.map((link) => ({ title: link.title, url: link.url }));
  if (take('room_id', file.room_id !== side.room_id)) lesson.room = roomKeyOf(rooms, side.room_id);
  if (take('requires_paid_plan', file.requires_paid_plan !== side.requires_paid_plan)) lesson.requires_paid_plan = side.requires_paid_plan;
  if (take('published', file.published !== side.published)) lesson.published = side.published;
}

/** The server's course into a course folder, as `takeLesson` takes a lesson. */
function takeCourse(folder: CourseFolder, server: CommunityCourseDraft, fields?: ReadonlySet<string>): void {
  const file = courseContent(folder);
  const side = courseContent(server);
  const take = (field: string, differs: boolean) => differs && (fields === undefined || fields.has(field));
  if (take('title', file.title !== side.title)) folder.title = server.title;
  if (take('description', file.description !== side.description)) folder.description = server.description;
  if (take('published', file.published !== side.published)) folder.published = side.published;
}

const listed = <E extends { id?: string }>(entries: readonly E[]): Array<Listed<E>> => listedIds(entries, (entry) => entry.id);

// ── The plan: every entity's, every order's and every lesson's place's verdict ──

/** Whose children an order orders: the community's courses, a course's modules, a module's lessons. */
type OrderKind = 'courses' | 'modules' | 'lessons';

interface OrderPlan {
  state: SyncState;
  /** The server's order of the parent's children, as ids. */
  server: string[];
  /** The order the files were last synced from (their `synced:` list); none recorded: none. */
  base: string[] | undefined;
}

interface Verdict<S> {
  state: SyncState;
  server: S;
  serverRev: string;
}

/**
 * Where a lesson stands among the modules of its course, three-way: the module whose synced list
 * holds it (`base`), the one the files list it in (`files`; a stand-in for a module push creates),
 * and the one the server holds it in.
 */
interface Placement {
  state: SyncState;
  base: string | undefined;
  files: string;
  server: string;
}

interface LessonPlan {
  course: CourseFolder;
  module: ModuleEntry;
  lesson: LessonFile;
  /** Null: push creates it. */
  server: Verdict<ExportLesson> | null;
  placement: Placement | null;
}

interface ModulePlan {
  course: CourseFolder;
  entry: ModuleEntry;
  server: Verdict<ServerModule> | null;
  lessonsOrder: OrderPlan | null;
}

interface CoursePlan {
  folder: CourseFolder;
  server: Verdict<CommunityCourseDraft> | null;
  export: CommunityCourseExport | null;
  modulesOrder: OrderPlan | null;
  modules: ModulePlan[];
  lessons: LessonPlan[];
  /** On the server, in this course, named nowhere in the files: left as is by push, added by pull. */
  untrackedModules: ServerModule[];
  untrackedLessons: ExportLesson[];
}

export interface CoursesPlan {
  server: ServerCourses;
  order: OrderPlan;
  courses: CoursePlan[];
  /** Courses on the server that `courses:` does not list. */
  untracked: CommunityCourseExport[];
  /** Ids the server does not have where the files put them: refused by both verbs. */
  problems: string[];
}

/**
 * An order's verdict (the Sync rules table), against its exact base: the parent's children as of
 * the last sync. It compares the children the files list that the base and the server also hold,
 * in each one's order. A child the files name nowhere — made in the web since, or left out of the
 * list — takes no part; one that joined or left the parent on one side is its place's verdict
 * (`placementVerdict`), not the order's. One both sides added since (a lesson moved in on both)
 * has no base here: both must put it in the same gap among the others, or it is a conflict. With
 * no base recorded, the sides agree or conflict.
 */
export function orderVerdict(list: readonly string[], base: readonly string[] | undefined, server: readonly string[]): SyncState {
  const onServer = new Set(server);
  if (base === undefined) {
    const shared = list.filter((id) => onServer.has(id));
    return sameOrder(shared, restrict(server, shared)) ? 'converged' : 'conflict';
  }
  const inBase = new Set(base);
  const shared = list.filter((id) => inBase.has(id) && onServer.has(id));
  const added = list.filter((id) => !inBase.has(id) && onServer.has(id));
  if (added.length > 0 && !sameGaps(added, shared, list, server)) return 'conflict';
  const was = restrict(base, shared);
  const now = restrict(server, shared);
  return threeWay(!sameOrder(shared, was), !sameOrder(now, was), sameOrder(shared, now));
}

/** Whether `a` and `b` put each of `added` after the same ones of `shared`, and `added` in one order. */
function sameGaps(added: readonly string[], shared: readonly string[], a: readonly string[], b: readonly string[]): boolean {
  const before = (order: readonly string[], id: string) => new Set(order.slice(0, order.indexOf(id)).filter((other) => shared.includes(other)));
  const same = (x: Set<string>, y: Set<string>) => x.size === y.size && [...x].every((id) => y.has(id));
  return sameOrder(restrict(a, added), restrict(b, added)) && added.every((id) => same(before(a, id), before(b, id)));
}

/**
 * A lesson's place's verdict (the Sync rules table, per lesson): the module the files list it in,
 * its base module and the server's. Moved to different modules on both sides is a conflict.
 */
export function placementVerdict(files: string, base: string | undefined, server: string): SyncState {
  if (base === undefined) return files === server ? 'converged' : 'conflict';
  return threeWay(files !== base, server !== base, files === server);
}

function planOrder(entries: ReadonlyArray<{ id?: string }>, server: string[], base: string[] | undefined): OrderPlan {
  return { state: orderVerdict([...idsOf(entries)], base, server), server: [...server], base: base && [...base] };
}

/** A lesson's base module: the one module of the course whose synced list holds it. */
function baseModule(synced: CourseSynced | undefined, lessonId: string): string | undefined {
  const holding = Object.entries(synced?.lessons ?? {}).filter(([, ids]) => ids.includes(lessonId));
  return holding.length === 1 ? holding[0][0] : undefined;
}

function courseName(files: CommunityFiles, server: ServerCourses, courseId: string): string {
  const folder = files.courses.find((course) => course.id === courseId);
  return folder ? `course ${folder.slug}` : `course ${show(server.course.get(courseId)?.course.title ?? courseId)}`;
}

export function planCourses(files: CommunityFiles, server: ServerCourses): CoursesPlan {
  const problems: string[] = [];
  const named = idsOf(files.courses);
  return {
    server,
    order: planOrder(files.courses, server.ids, files.synced_courses),
    courses: files.courses.map((folder) => planCourse(files, folder, server, problems)),
    untracked: server.courses.filter((course) => !named.has(course.course.course_id)),
    problems,
  };
}

function planCourse(files: CommunityFiles, folder: CourseFolder, server: ServerCourses, problems: string[]): CoursePlan {
  const found = folder.id === undefined ? undefined : server.course.get(folder.id);
  if (folder.id !== undefined && !found) problems.push(`course ${folder.slug}: ${folder.id} is not a course of this community`);
  const courseId = found?.course.course_id;
  const plan: CoursePlan = {
    folder, server: null, export: found ?? null, modulesOrder: null, modules: [], lessons: [], untrackedModules: [], untrackedLessons: [],
  };
  const moduleIds = idsOf(folder.modules);
  const lessonIds = idsOf(folder.modules.flatMap((module) => module.lessons));
  if (found) {
    const side = serverCourse(found.course);
    plan.server = { state: classify(folder.rev, courseRev(found.course.course_id, courseContent(folder)), side.rev), server: found.course, serverRev: side.rev };
    plan.modulesOrder = planOrder(folder.modules, found.modules.map((m) => m.module_id), folder.synced?.modules);
    plan.untrackedModules = found.modules.filter((module) => !moduleIds.has(module.module_id));
    plan.untrackedLessons = found.modules.flatMap((module) => module.lessons).filter((lesson) => !lessonIds.has(lesson.lesson_id));
  }
  // An id is checked against where the server holds it: the API moves neither a module nor a lesson between courses.
  const elsewhere = (what: string, at: string, id: string, owner: string | undefined) => {
    if (owner === undefined) problems.push(`${what} ${at}: ${id} is not a ${what} of this community`);
    else problems.push(`${what} ${at}: ${what} ${id} is one of ${courseName(files, server, owner)}'s on the server, and a ${what} does not move between courses`);
  };
  for (const entry of folder.modules) {
    const at = `${folder.slug}/${entry.key}`;
    const module = entry.id === undefined ? undefined : server.module.get(entry.id);
    if (entry.id !== undefined && module?.courseId !== courseId) elsewhere('module', at, entry.id, module?.courseId);
    const mine = module && module.courseId === courseId ? module.module : undefined;
    const modulePlan: ModulePlan = { course: folder, entry, server: null, lessonsOrder: null };
    if (mine) {
      const side = serverModule(mine);
      modulePlan.server = { state: classify(entry.rev, moduleRev(mine.module_id, moduleContent(entry)), side.rev), server: mine, serverRev: side.rev };
      modulePlan.lessonsOrder = planOrder(entry.lessons, mine.lessons.map((l) => l.lesson_id), folder.synced?.lessons[mine.module_id]);
    }
    plan.modules.push(modulePlan);
    for (const lesson of entry.lessons) {
      const where = server.lesson.get(lesson.id ?? '');
      if (lesson.id !== undefined && where?.courseId !== courseId) elsewhere('lesson', `${folder.slug}/${lesson.slug}`, lesson.id, where?.courseId);
      const lessonPlan: LessonPlan = { course: folder, module: entry, lesson, server: null, placement: null };
      if (where && where.courseId === courseId) {
        const side = serverLesson(where.lesson);
        lessonPlan.server = { state: classify(lesson.rev, lessonRev(where.lesson.lesson_id, fileLesson(files.rooms, lesson)), side.rev), server: where.lesson, serverRev: side.rev };
        const filesModule = plannedIds.module(folder, entry)!;
        const base = baseModule(folder.synced, where.lesson.lesson_id);
        lessonPlan.placement = { state: placementVerdict(filesModule, base, where.moduleId), base, files: filesModule, server: where.moduleId };
      }
      plan.lessons.push(lessonPlan);
    }
  }
  return plan;
}

/** The caps, from the read and before any write (the Caps table), counting each create the server may answer by replay once. */
export function courseCapProblems(files: CommunityFiles, plan: CoursesPlan): string[] {
  const problems: string[] = [];
  const title = (entity: { title: string }) => toLf(entity.title).trim();
  const courseCreates = plan.courses.filter((course) => course.folder.id === undefined).map((course) => title(course.folder));
  const courses = plan.server.courses.length;
  if (courses + certainlyNew(courseCreates, plan.untracked.map((course) => title(course.course))) > COMMUNITY_COURSES_MAX) {
    problems.push(`courses: a community holds at most ${COMMUNITY_COURSES_MAX} courses, drafts included; it has ${courses}, and the files create ${courseCreates.length}`);
  }
  const named = idsOf(files.courses.flatMap((course) => course.modules.flatMap((module) => module.lessons)));
  const lessonCreates: string[] = [];
  for (const course of plan.courses) {
    const moduleCreates = course.modules.filter((module) => module.entry.id === undefined).map((module) => title(module.entry));
    const creates = course.lessons.filter((lesson) => lesson.lesson.id === undefined).map((lesson) => title(lesson.lesson));
    lessonCreates.push(...creates);
    const modules = course.export?.modules.length ?? 0;
    const lessons = course.export?.modules.reduce((n, module) => n + module.lessons.length, 0) ?? 0;
    if (modules + certainlyNew(moduleCreates, course.untrackedModules.map(title)) > COMMUNITY_COURSE_MODULES_MAX) {
      problems.push(`course ${course.folder.slug}: a course holds at most ${COMMUNITY_COURSE_MODULES_MAX} modules; it has ${modules}, and the files create ${moduleCreates.length}`);
    }
    if (lessons + certainlyNew(creates, course.untrackedLessons.map(title)) > COMMUNITY_COURSE_LESSONS_MAX) {
      problems.push(`course ${course.folder.slug}: a course holds at most ${COMMUNITY_COURSE_LESSONS_MAX} lessons, drafts included; it has ${lessons}, and the files create ${creates.length}`);
    }
  }
  const lessons = plan.server.lesson.size;
  const untracked = [...plan.server.lesson.values()].filter(({ lesson }) => !named.has(lesson.lesson_id)).map(({ lesson }) => title(lesson));
  if (lessons + certainlyNew(lessonCreates, untracked) > COMMUNITY_LESSONS_MAX) {
    problems.push(`lessons: a community holds at most ${COMMUNITY_LESSONS_MAX} lessons, drafts included; it has ${lessons}, and the files create ${lessonCreates.length}`);
  }
  return problems;
}

/**
 * How many of the creates (`creates`, their titles) are certainly new. A create may be the retry
 * of one whose answer was lost, which the server answers with what it made then: an entity the
 * files name nowhere (`untracked`, their titles), under the title that create sent. Each such
 * entity explains one create of its title; an entity made in the web under another title explains
 * none, so creates past a cap it fills are refused before any write.
 */
function certainlyNew(creates: readonly string[], untracked: readonly string[]): number {
  const left = [...untracked];
  return creates.filter((title) => {
    const at = left.indexOf(title);
    if (at >= 0) left.splice(at, 1);
    return at < 0;
  }).length;
}

// ── Places: the server's order of every parent, as the server computes it ──

/** One parent's children in the server's order, and where push stands with it. */
interface OrderSim {
  server: string[];
  /**
   * While only the server changed the parent's order (`remote`): the files' synced list, which push
   * records as it stands — a create extends it, a lesson push moves away leaves it — instead of the
   * server's order, so the server's change is still there for pull to take.
   */
  pinned: string[] | null;
  /** Both sides changed it: push refuses, and writes no place in it. */
  conflict: boolean;
  /** A write changed it since the read: its synced list is recorded again. */
  touched: boolean;
}

/** Every parent's order, kept as the server's own placement leaves it after each write. */
class Sim {
  constructor(
    readonly courses: OrderSim,
    readonly modules = new Map<string, OrderSim>(),
    readonly lessons = new Map<string, OrderSim>(),
    /** Each lesson's module on the server. */
    readonly at = new Map<string, string>(),
  ) {}

  static from(plan: CoursesPlan): Sim {
    const order = (ids: string[], verdict: OrderPlan | null | undefined): OrderSim => ({
      server: [...ids],
      pinned: verdict?.state === 'remote' ? [...(verdict.base ?? [])] : null,
      conflict: verdict?.state === 'conflict',
      touched: false,
    });
    const sim = new Sim(order(plan.order.server, plan.order));
    for (const course of plan.server.courses) {
      const courseId = course.course.course_id;
      const tracked = plan.courses.find((c) => c.export?.course.course_id === courseId);
      sim.modules.set(courseId, order(course.modules.map((m) => m.module_id), tracked?.modulesOrder));
      for (const module of course.modules) {
        const entry = tracked?.modules.find((m) => m.server?.server.module_id === module.module_id);
        sim.lessons.set(module.module_id, order(module.lessons.map((l) => l.lesson_id), entry?.lessonsOrder));
        for (const lesson of module.lessons) sim.at.set(lesson.lesson_id, module.module_id);
      }
    }
    return sim;
  }

  clone(): Sim {
    const copy = (order: OrderSim): OrderSim => ({ ...order, server: [...order.server], pinned: order.pinned && [...order.pinned] });
    const map = (orders: Map<string, OrderSim>) => new Map([...orders].map(([id, order]) => [id, copy(order)]));
    return new Sim(copy(this.courses), map(this.modules), map(this.lessons), new Map(this.at));
  }
}

/** A new parent: created by this push, its order the server's. */
const freshOrder = (): OrderSim => ({ server: [], pinned: null, conflict: false, touched: true });

/** A create lands last: on the server, and in the files' synced list while only the server changed the order. */
function append(order: OrderSim, id: string): void {
  order.server.push(id);
  order.pinned?.push(id);
  order.touched = true;
}

/** `item` into `list` after the nearest sibling before it in `order` that `list` holds, or first. */
function insertAfter<T>(list: T[], item: T, order: readonly T[]): void {
  let at = 0;
  for (let i = order.indexOf(item) - 1; i >= 0; i--) {
    const k = list.indexOf(order[i]);
    if (k >= 0) {
      at = k + 1;
      break;
    }
  }
  list.splice(at, 0, item);
}

/** A position write: the server's placement. */
function shift(order: OrderSim, id: string, position: number): void {
  placeAt(order.server, id, position);
  order.touched = true;
}

/** A lesson leaving its module for another. */
function leave(order: OrderSim, id: string): void {
  for (const list of [order.server, order.pinned ?? []]) {
    const at = list.indexOf(id);
    if (at >= 0) list.splice(at, 1);
  }
  order.touched = true;
}

/** Each entity's id: its own once it has one; a stand-in while push has yet to create it (the plan's prediction). */
interface Ids {
  course(course: CourseFolder): string | undefined;
  module(course: CourseFolder, module: ModuleEntry): string | undefined;
  lesson(course: CourseFolder, lesson: LessonFile): string | undefined;
}

const realIds: Ids = { course: (c) => c.id, module: (_c, m) => m.id, lesson: (_c, l) => l.id };

const plannedIds: Ids = {
  course: (c) => c.id ?? `new:course:${c.slug}`,
  module: (c, m) => m.id ?? `new:module:${c.slug}/${m.key}`,
  lesson: (c, l) => l.id ?? `new:lesson:${c.slug}/${l.slug}`,
};

/** A parent the files list children of: its order on the server (`sim`), and the children the files list in it. */
interface Parent {
  what: string;
  kind: OrderKind;
  id: string;
  order: OrderSim;
  /** The children the files list, by id, in list order. */
  listed: string[];
  /** A module's lessons: its course, whose modules lessons move between. */
  courseId: string | null;
}

function parents(files: CommunityFiles, sim: Sim, ids: Ids): Parent[] {
  const out: Parent[] = [];
  const listedOf = <E>(entries: readonly E[], id: (entry: E) => string | undefined) => [...idsOf(listedIds(entries, id))];
  out.push({ what: 'courses order', kind: 'courses', id: files.community_id, order: sim.courses, courseId: null, listed: listedOf(files.courses, ids.course) });
  for (const course of files.courses) {
    const courseId = ids.course(course);
    const order = courseId === undefined ? undefined : sim.modules.get(courseId);
    if (courseId === undefined || !order) continue;
    out.push({
      what: `modules order of course ${course.slug}`, kind: 'modules', id: courseId, order, courseId,
      listed: listedOf(course.modules, (module) => ids.module(course, module)),
    });
  }
  for (const course of files.courses) {
    const courseId = ids.course(course);
    if (courseId === undefined || !sim.modules.has(courseId)) continue;
    for (const module of course.modules) {
      const moduleId = ids.module(course, module);
      const order = moduleId === undefined ? undefined : sim.lessons.get(moduleId);
      if (moduleId === undefined || !order) continue;
      out.push({
        what: `lessons order of module ${course.slug}/${module.key}`, kind: 'lessons', id: moduleId, order, courseId,
        listed: listedOf(module.lessons, (lesson) => ids.lesson(course, lesson)),
      });
    }
  }
  return out;
}

function listedIds<E>(entries: readonly E[], id: (entry: E) => string | undefined): Array<Listed<E>> {
  return entries.map((entry) => ({ entry, id: id(entry) }));
}

/** One position write of the places phase: a lesson's names the module it goes to, in its course. */
type PlaceMove =
  | { kind: 'course'; id: string; position: number }
  | { kind: 'module'; id: string; courseId: string; position: number }
  | { kind: 'lesson'; id: string; courseId: string; moduleId: string; position: number };

interface Places {
  moves: PlaceMove[];
  conflicts: string[];
  /** What each reordered parent goes from and to, by name. */
  reorders: Array<{ what: string; from: string[]; to: string[] }>;
}

/**
 * The position writes that give every parent the files' order where the server's order is the
 * files' to set — every parent but a conflict, and one whose order changed on the server only,
 * which push leaves (a create placed mid-list in one, or a lesson moved into it, is a conflict) —
 * applied to `sim` as the server applies them. Of a module's listed lessons, one the server moved
 * elsewhere since stays there; of its lessons on the server, one the files move elsewhere is the
 * other module's to place, and one the files name nowhere or the server moved in stays where it is
 * (`mergeOrder`). Every parent's verdict is taken before any write: moving a lesson changes two.
 */
function computePlaces(files: CommunityFiles, sim: Sim, ids: Ids, placements: ReadonlyMap<string, Placement>): Places {
  const out: Places = { moves: [], conflicts: [], reorders: [] };
  const placementOf = (id: string) => placements.get(id);
  for (const parent of parents(files, sim, ids)) {
    const { order } = parent;
    if (order.conflict) continue;
    const lessons = parent.kind === 'lessons';
    // The children push gives the files' place in this parent, and the ones it takes elsewhere.
    const wanted = lessons ? parent.listed.filter((id) => placementOf(id)?.state !== 'remote') : parent.listed;
    const leaving = lessons ? order.server.filter((id) => placementOf(id)?.state === 'local' && !parent.listed.includes(id)) : [];
    const arriving = wanted.filter((id) => !order.server.includes(id));
    if (order.pinned) {
      const kept = order.pinned;
      const shared = wanted.filter((id) => kept.includes(id));
      if (arriving.length > 0 || !sameOrder(shared, restrict(kept, shared))) out.conflicts.push(parent.what);
      continue;
    }
    if (arriving.length === 0 && sameOrder(restrict(order.server, wanted), wanted)) continue;
    const target = filesOrder(wanted.map((id) => ({ entry: id, id })), order.server, new Set([...wanted, ...leaving]));
    const from = [...order.server];
    for (const move of reorderMoves(order.server, target)) {
      if (lessons) {
        const left = sim.at.get(move.id);
        if (left === undefined) throw new Error(`computePlaces: lesson ${move.id} is in no module`);
        if (left !== parent.id) leave(sim.lessons.get(left)!, move.id);
        sim.at.set(move.id, parent.id);
      }
      shift(order, move.id, move.position);
      const { id, position } = move;
      out.moves.push(
        parent.kind === 'courses' ? { kind: 'course', id, position }
          : parent.kind === 'modules' ? { kind: 'module', id, courseId: parent.id, position }
            : { kind: 'lesson', id, courseId: parent.courseId!, moduleId: parent.id, position },
      );
    }
    out.reorders.push({ what: parent.what, from, to: target });
  }
  return out;
}

// ── Push ──

/** Where push records what each write changed, as soon as it returns: each file on its own. */
export interface CourseSaver {
  /** `community.yaml`, where the courses' synced list is kept. */
  community(): void;
  course(course: CourseFolder): void;
  lesson(course: CourseFolder, lesson: LessonFile): void;
}

/** The authoring writes, each answering the entity as it now stands. */
export interface CourseRemote {
  createCourse(body: CommunityCourseCreateBody): Promise<CommunityCourseDraft>;
  updateCourse(courseId: string, body: CommunityCourseUpdateBody): Promise<CommunityCourseDraft>;
  createModule(courseId: string, body: CommunityModuleCreateBody): Promise<CommunityModuleDraft>;
  updateModule(courseId: string, moduleId: string, body: CommunityModuleUpdateBody): Promise<CommunityModuleDraft>;
  createLesson(courseId: string, body: CommunityLessonCreateBody): Promise<CommunityLessonDraft>;
  updateLesson(courseId: string, lessonId: string, body: CommunityLessonUpdateBody): Promise<CommunityLessonDraft>;
  /** The reads that learn where a position write whose answer was lost left the server. */
  listCourses(): Promise<CommunityCourseDraft[]>;
  exportCourse(courseId: string): Promise<CommunityCourseExport>;
}

/**
 * A write's failure that may have reached the server — an answer lost (a 5xx, the connection) —
 * not a refusal: a 4xx, as the client raises it or as `communitiesFailure` words it.
 */
function outcomeUnknown(err: unknown): boolean {
  return (err as { isExpected?: unknown } | null)?.isExpected !== true;
}

/** The fields of a course's write that differ from the server: `published: true` only in the publish phase, which comes last. */
function coursePatch(folder: CourseFolder, now: CommunityCourseDraft, publish: boolean): CommunityCourseUpdateBody | null {
  const file = courseContent(folder);
  const server = courseContent(now);
  const body: CommunityCourseUpdateBody = {
    ...(file.title !== server.title ? { title: folder.title } : {}),
    ...(file.description !== server.description ? { description: folder.description } : {}),
    ...(file.published !== server.published && (publish || !file.published) ? { published: file.published } : {}),
  };
  return Object.keys(body).length > 0 ? body : null;
}

/**
 * The fields of a lesson's write that differ from the server, raw (the URL, never the video it
 * names; never an unchanged text, which the server parses on every save). A room this push has yet
 * to create is left to the write after it (`waitForRooms`).
 */
function lessonPatch(rooms: RoomEntry[], lesson: LessonFile, now: ServerLesson, waitForRooms: boolean): CommunityLessonUpdateBody | null {
  const file = fileLesson(rooms, lesson);
  const server = lessonContent(now);
  const pending = file.room_id?.startsWith(PENDING_ROOM) ?? false;
  if (pending && !waitForRooms) throw new Error(`lessonPatch: room ${lesson.room} has no id yet`);
  const body: CommunityLessonUpdateBody = {
    ...(file.title !== server.title ? { title: lesson.title } : {}),
    ...(file.body !== server.body ? { body: lesson.body } : {}),
    ...(!isDeepStrictEqual(file.video, server.video) ? { video_url: lesson.video_url ?? null } : {}),
    ...(!isDeepStrictEqual(file.links, server.links) ? { links: lesson.links } : {}),
    ...(file.room_id !== server.room_id && !pending ? { room_id: file.room_id } : {}),
    ...(file.requires_paid_plan !== server.requires_paid_plan ? { requires_paid_plan: lesson.requires_paid_plan } : {}),
    ...(file.published !== server.published ? { published: lesson.published } : {}),
  };
  return Object.keys(body).length > 0 ? body : null;
}

function courseChanges(from: CourseContent, to: CourseContent): string[] {
  return [
    ...(from.title !== to.title ? [`title ${show(from.title)} → ${show(to.title)}`] : []),
    ...(from.description !== to.description ? ['description'] : []),
    ...(from.published !== to.published ? [to.published ? 'published' : 'unpublished'] : []),
  ];
}

function lessonChanges(from: LessonContent, to: LessonContent, room: (id: string | null) => string): string[] {
  return [
    ...(from.title !== to.title ? [`title ${show(from.title)} → ${show(to.title)}`] : []),
    ...(from.body !== to.body ? ['text'] : []),
    ...(!isDeepStrictEqual(from.video, to.video) ? [to.video ? (from.video ? 'video' : 'video added') : 'video removed'] : []),
    ...(!isDeepStrictEqual(from.links, to.links) ? ['links'] : []),
    ...(from.room_id !== to.room_id ? [`room ${room(from.room_id)} → ${room(to.room_id)}`] : []),
    ...(from.requires_paid_plan !== to.requires_paid_plan ? [to.requires_paid_plan ? 'needs a paid plan' : 'no longer needs a paid plan'] : []),
    ...(from.published !== to.published ? [to.published ? 'published' : 'unpublished'] : []),
  ];
}

/** The server's checks of a lesson's text, by the contract's reasons: each refuses a write with a 400. */
const LESSON_TEXT_REASONS: ReadonlyArray<string> = [
  COMMUNITY_LESSON_LINE_PREFIX_TOO_WIDE_REASON,
  COMMUNITY_LESSON_TREE_TOO_DEEP_REASON,
  COMMUNITY_LESSON_TREE_TOO_LARGE_REASON,
];

/** A lesson's write, a refusal of its text naming the file to change. */
async function lessonWrite(course: CourseFolder, lesson: LessonFile, write: () => Promise<void>): Promise<void> {
  try {
    await write();
  } catch (err) {
    const reason = extractApiErrorReason(err);
    if (reason === null || !LESSON_TEXT_REASONS.includes(reason)) throw err;
    throw new Error(`${extractApiMessage(err)} — change the lesson's text in ${lessonPath(course, lesson)}`);
  }
}

/** A course's push: its plan, the server's state as the writes leave it, and the writes of each phase. */
export class CoursePush {
  private readonly sim: Sim;
  private readonly courseNow = new Map<string, CommunityCourseDraft>();
  private readonly moduleNow = new Map<string, CommunityModuleDraft>();
  private readonly lessonNow = new Map<string, ServerLesson>();
  private readonly names = new Map<string, string>();
  /** Files whose synced lists changed since they were last saved. */
  private readonly unsaved = new Set<CourseFolder | null>();
  /** Each listed lesson's place's verdict, by id. */
  private readonly placements = new Map<string, Placement>();
  /** The lessons the server moved since, which push leaves where they are: by module, those it moved in and out. */
  private readonly movedIn = new Map<string, Set<string>>();
  private readonly movedOut = new Map<string, string[]>();
  /** The places phase as the plan predicts it, every create landing as a new entity. */
  readonly predicted: Places;

  constructor(
    private readonly files: CommunityFiles,
    private readonly plan: CoursesPlan,
    private readonly remote: CourseRemote,
    private readonly save: CourseSaver,
  ) {
    this.sim = Sim.from(plan);
    for (const lesson of plan.courses.flatMap((course) => course.lessons)) {
      const { placement } = lesson;
      if (!placement || lesson.lesson.id === undefined) continue;
      this.placements.set(lesson.lesson.id, placement);
      if (placement.state !== 'remote') continue;
      this.movedIn.set(placement.server, new Set([...(this.movedIn.get(placement.server) ?? []), lesson.lesson.id]));
      if (placement.base !== undefined) this.movedOut.set(placement.base, [...(this.movedOut.get(placement.base) ?? []), lesson.lesson.id]);
    }
    for (const course of plan.server.courses) {
      this.courseNow.set(course.course.course_id, course.course);
      this.names.set(course.course.course_id, show(course.course.title));
      for (const module of course.modules) {
        this.moduleNow.set(module.module_id, module);
        this.names.set(module.module_id, show(module.title));
        for (const lesson of module.lessons) {
          this.lessonNow.set(lesson.lesson_id, lesson);
          this.names.set(lesson.lesson_id, show(lesson.title));
        }
      }
    }
    for (const course of files.courses) {
      this.names.set(plannedIds.course(course)!, course.slug);
      for (const module of course.modules) {
        this.names.set(plannedIds.module(course, module)!, module.key);
        for (const lesson of module.lessons) this.names.set(plannedIds.lesson(course, lesson)!, lesson.slug);
      }
    }
    this.predicted = this.predict();
  }

  private get communityId(): string {
    return this.files.community_id;
  }

  private lessons(): LessonPlan[] {
    return this.plan.courses.flatMap((course) => course.lessons);
  }

  /** The places phase on a copy of the server's orders where every create has landed, last. */
  private predict(): Places {
    const sim = this.sim.clone();
    for (const course of this.files.courses) {
      const courseId = plannedIds.course(course)!;
      if (course.id === undefined) {
        append(sim.courses, courseId);
        sim.modules.set(courseId, freshOrder());
      }
      for (const module of course.modules) {
        const moduleId = plannedIds.module(course, module)!;
        if (module.id === undefined) {
          const modules = sim.modules.get(courseId);
          if (!modules) continue;
          append(modules, moduleId);
          sim.lessons.set(moduleId, freshOrder());
        }
        for (const lesson of module.lessons) {
          const lessons = sim.lessons.get(moduleId);
          if (lesson.id !== undefined || !lessons) continue;
          const lessonId = plannedIds.lesson(course, lesson)!;
          append(lessons, lessonId);
          sim.at.set(lessonId, moduleId);
        }
      }
    }
    return computePlaces(this.files, sim, plannedIds, this.placements);
  }

  // ── The listing ──

  private roomName = (id: string | null): string => roomName(this.files.rooms, id);

  /** Push's listing of the courses, server → files. */
  list(listing: Listing): void {
    const { plan, files } = this;
    for (const course of plan.courses) {
      const { folder } = course;
      if (!course.server) {
        listing.changes.push(`+ course ${folder.slug}: create ${show(folder.title)}, ${folder.published ? 'published once its lessons are in place' : 'a draft'}`);
      } else if (course.server.state === 'local') {
        listing.changes.push(`~ course ${folder.slug}: ${courseChanges(courseContent(course.server.server), courseContent(folder)).join('; ')}`);
      }
      for (const module of course.modules) {
        const at = `${folder.slug}/${module.entry.key}`;
        if (!module.server) listing.changes.push(`+ module ${at}: create ${show(module.entry.title)}`);
        else if (module.server.state === 'local') listing.changes.push(`~ module ${at}: title ${show(module.server.server.title)} → ${show(module.entry.title)}`);
      }
      for (const lesson of course.lessons) {
        const at = `${folder.slug}/${lesson.lesson.slug}`;
        const file = fileLesson(files.rooms, lesson.lesson);
        if (!lesson.server) {
          const flags = [...(file.published ? ['published'] : []), ...(file.room_id ? [`room ${this.roomName(file.room_id)}`] : [])];
          listing.changes.push(`+ lesson ${at}: create ${show(lesson.lesson.title)} in module ${lesson.module.key}${flags.length > 0 ? `, ${flags.join(', ')}` : ''}`);
        } else if (lesson.server.state === 'local') {
          const server = lessonContent(lesson.server.server);
          listing.changes.push(`~ lesson ${at}: ${lessonChanges(server, file, this.roomName).join('; ')}`);
          if (server.body !== file.body) listing.changes.push(...lineDiff(server.body, file.body).map((line) => `    ${line}`));
        }
      }
    }
    for (const reorder of this.predicted.reorders) {
      listing.changes.push(`~ ${reorder.what}: ${reorder.from.map(this.name).join(', ')} → ${reorder.to.map(this.name).join(', ')}`);
    }
    const verdicts = (state: SyncState) => [
      ...(plan.order.state === state ? ['courses order'] : []),
      ...plan.courses.flatMap((course) => [
        ...(course.server?.state === state ? [`course ${course.folder.slug}`] : []),
        ...(course.modulesOrder?.state === state ? [`modules order of course ${course.folder.slug}`] : []),
        ...course.modules.flatMap((module) => [
          ...(module.server?.state === state ? [`module ${course.folder.slug}/${module.entry.key}`] : []),
          ...(module.lessonsOrder?.state === state ? [`lessons order of module ${course.folder.slug}/${module.entry.key}`] : []),
        ]),
        ...course.lessons.flatMap((lesson) => [
          ...(lesson.server?.state === state ? [`lesson ${course.folder.slug}/${lesson.lesson.slug}`] : []),
          ...(lesson.placement?.state === state
            ? [`lesson ${course.folder.slug}/${lesson.lesson.slug}: ${state === 'conflict' ? 'moved in the files and on the server' : `moved to module ${this.pathOf(lesson.placement.server)}`}`]
            : []),
        ]),
      ]),
    ];
    listing.serverChanged.push(...verdicts('remote'));
    listing.conflicts.push(...new Set([...verdicts('conflict'), ...this.predicted.conflicts]));
    listing.leftAsIs.push(
      ...plan.untracked.map((course) => `course ${show(course.course.title)} (${course.course.course_id})`),
      ...plan.courses.flatMap((course) => [
        ...course.untrackedModules.map((module) => `module ${show(module.title)} (${module.module_id}) of course ${course.folder.slug}`),
        ...course.untrackedLessons.map((lesson) => `lesson ${show(lesson.title)} (${lesson.lesson_id}) of course ${course.folder.slug}`),
      ]),
    );
  }

  private name = (id: string): string => this.names.get(id) ?? id;

  /** Revs to refresh without a write: both sides agree, or the files carried no base. Saves the files they live in. */
  refreshConverged(): number {
    const { plan, files } = this;
    let refreshed = 0;
    const refresh = (converged: boolean, owner: CourseFolder | null, set: () => void) => {
      if (!converged) return;
      set();
      this.unsaved.add(owner);
      refreshed++;
    };
    // An order or a place both sides changed alike takes the server's as its base (`settle`).
    const rebase = (converged: boolean, order: OrderSim | undefined) => {
      if (!converged || !order) return;
      order.touched = true;
      refreshed++;
    };
    rebase(plan.order.state === 'converged', this.sim.courses);
    for (const course of plan.courses) {
      const { folder } = course;
      refresh(course.server?.state === 'converged', folder, () => { folder.rev = course.server!.serverRev; });
      if (folder.id !== undefined) rebase(course.modulesOrder?.state === 'converged', this.sim.modules.get(folder.id));
      for (const module of course.modules) {
        refresh(module.server?.state === 'converged', folder, () => { module.entry.rev = module.server!.serverRev; });
        if (module.entry.id !== undefined) rebase(module.lessonsOrder?.state === 'converged', this.sim.lessons.get(module.entry.id));
      }
      for (const lesson of course.lessons) {
        const placement = lesson.placement;
        if (placement?.state !== 'converged') continue;
        // Moved alike on both sides: its base module becomes the one both hold it in.
        const was = placement.base === undefined ? undefined : this.sim.lessons.get(placement.base);
        if (was) {
          const at = was.pinned?.indexOf(lesson.lesson.id!) ?? -1;
          if (at >= 0) was.pinned!.splice(at, 1);
          was.touched = true;
        }
        const now = this.sim.lessons.get(placement.server);
        if (now) {
          if (now.pinned && !now.pinned.includes(lesson.lesson.id!)) insertAfter(now.pinned, lesson.lesson.id!, lesson.module.lessons.map((l) => l.id ?? ''));
          now.touched = true;
        }
        refreshed++;
      }
      for (const lesson of course.lessons) {
        if (lesson.server?.state === 'converged') {
          lesson.lesson.rev = lesson.server.serverRev;
          this.save.lesson(folder, lesson.lesson);
          refreshed++;
        }
      }
    }
    this.settle();
    return refreshed;
  }

  /** Save the files whose synced lists changed. */
  private flush(): void {
    for (const owner of this.unsaved) {
      if (owner === null) this.save.community();
      else this.save.course(owner);
    }
    this.unsaved.clear();
  }

  // ── The writes, phase by phase ──

  /** Courses, then lessons, that the files take off the community: first, before anything else changes. */
  unpublishWrites(): Write[] {
    const writes: Write[] = [];
    for (const course of this.plan.courses) {
      const { server, folder } = course;
      if (server?.state === 'local' && server.server.published && !folder.published) {
        writes.push({ label: `course ${folder.slug} (unpublish)`, requests: 1, apply: () => this.updateCourse(folder, false) });
      }
    }
    for (const lesson of this.lessons()) {
      const { server, lesson: file, course } = lesson;
      if (server?.state === 'local' && server.server.published && !file.published) {
        writes.push({ label: `lesson ${course.slug}/${file.slug} (unpublish)`, requests: 1, apply: () => lessonWrite(course, file, () => this.updateLesson(course, file, true)) });
      }
    }
    return writes;
  }

  /** New courses, as drafts; and the courses whose content changed, but for their publishing. */
  courseWrites(): Write[] {
    return this.plan.courses.flatMap((course): Write[] => {
      const { server, folder } = course;
      if (!server) return [{ label: `course ${folder.slug} (create)`, requests: 1, apply: () => this.createCourse(folder) }];
      if (server.state !== 'local' || server.server.published !== folder.published) return [];
      return [{ label: `course ${folder.slug}`, requests: 1, apply: () => this.updateCourse(folder, false) }];
    });
  }

  moduleWrites(): Write[] {
    return this.plan.courses.flatMap((course) =>
      course.modules.flatMap((module): Write[] => {
        const at = `${course.folder.slug}/${module.entry.key}`;
        if (!module.server) return [{ label: `module ${at} (create)`, requests: 1, apply: () => this.createModule(course.folder, module.entry) }];
        if (module.server.state !== 'local') return [];
        return [{ label: `module ${at}`, requests: 1, apply: () => this.updateModule(course.folder, module.entry) }];
      }));
  }

  lessonWrites(): Write[] {
    return this.lessons().flatMap((lesson): Write[] => {
      const { server, course, module, lesson: file } = lesson;
      const at = `${course.slug}/${file.slug}`;
      if (!server) return [{ label: `lesson ${at} (create)`, requests: 1, apply: () => lessonWrite(course, file, () => this.createLesson(course, module, file)) }];
      if (server.state !== 'local') return [];
      const unpublished = server.server.published && !file.published;
      // An unpublished lesson's write came first; what it left is a room this push creates in between.
      if (unpublished && !fileLesson(this.files.rooms, file).room_id?.startsWith(PENDING_ROOM)) return [];
      return [{ label: `lesson ${at}`, requests: 1, apply: () => lessonWrite(course, file, () => this.updateLesson(course, file, false)) }];
    });
  }

  /** How many position writes the plan predicts, every create landing as a new entity. */
  get plannedPlaces(): number {
    return this.predicted.moves.length;
  }

  /** The position writes, from the server's orders as the creates left them: when the phase is reached. */
  placeWrites(): Write[] {
    return computePlaces(this.files, this.sim.clone(), realIds, this.placements).moves.map((move) => ({
      label: move.kind === 'lesson' && this.sim.at.get(move.id) !== move.moduleId
        ? `lesson ${this.pathOf(move.id)} → module ${this.pathOf(move.moduleId)}`
        : `place of ${move.kind} ${this.pathOf(move.id)}`,
      requests: 1,
      apply: () => this.place(move),
    }));
  }

  /** Courses the files publish: last, once their lessons are in place. */
  publishWrites(): Write[] {
    return this.plan.courses.flatMap((course): Write[] => {
      const { folder, server } = course;
      const now = folder.id === undefined ? undefined : this.courseNow.get(folder.id);
      if (!folder.published || now?.published || (server !== null && server.state !== 'local')) return [];
      return [{ label: `course ${folder.slug} (publish)`, requests: 1, apply: () => this.updateCourse(folder, true) }];
    });
  }

  private pathOf(id: string): string {
    for (const course of this.files.courses) {
      if (course.id === id) return course.slug;
      for (const module of course.modules) {
        if (module.id === id) return `${course.slug}/${module.key}`;
        for (const lesson of module.lessons) if (lesson.id === id) return `${course.slug}/${lesson.slug}`;
      }
    }
    return this.name(id);
  }

  // ── Each write ──

  private async createCourse(folder: CourseFolder): Promise<void> {
    const made = await this.remote.createCourse({
      title: folder.title,
      description: folder.description,
      client_id: await createClientId(this.communityId, 'course', folder.slug),
    });
    const twin = this.files.courses.find((other) => other !== folder && other.id === made.course_id);
    if (twin) {
      throw expected(
        `the name ${folder.slug} already names course ${made.course_id}, which your files also list as ${twin.slug}: ` +
          `a course folder's name keeps naming its course. Remove one of the two from ${COMMUNITY_FILE}'s courses.`,
      );
    }
    const existed = this.sim.modules.get(made.course_id);
    // The id and the server's rev are recorded at once, the files' own content kept: a push stopped
    // before the reconcile below reads the course as changed in the files, and its rerun sends it.
    folder.id = made.course_id;
    folder.rev = serverCourse(made).rev;
    this.courseNow.set(made.course_id, made);
    if (!existed) {
      append(this.sim.courses, made.course_id);
      this.sim.modules.set(made.course_id, freshOrder());
    }
    this.unsaved.add(folder);
    this.settle();
    // Reconcile: the answer may be a course made by an earlier attempt, and changed since.
    await this.updateCourse(folder, false);
  }

  private async updateCourse(folder: CourseFolder, publish: boolean): Promise<void> {
    const now = this.courseNow.get(folder.id!)!;
    const body = coursePatch(folder, now, publish);
    if (!body) return;
    const answer = await this.remote.updateCourse(folder.id!, body);
    takeCourse(folder, answer, new Set(Object.keys(body)));
    folder.rev = serverCourse(answer).rev;
    this.courseNow.set(answer.course_id, answer);
    this.save.course(folder);
  }

  private async createModule(course: CourseFolder, entry: ModuleEntry): Promise<void> {
    const courseId = course.id!;
    const made = await this.remote.createModule(courseId, {
      title: entry.title,
      client_id: await createClientId(this.communityId, 'module', `${course.slug}/${entry.key}`),
    });
    if (made.course_id !== courseId) {
      throw expected(
        `the key ${entry.key} already names module ${show(made.title)} (${made.module_id}) of ${courseName(this.files, this.plan.server, made.course_id)}: ` +
          'a key keeps naming its module, and a module does not move between courses. Give the new module another key.',
      );
    }
    const twin = this.files.courses.flatMap((c) => c.modules.map((m) => ({ c, m }))).find(({ m }) => m !== entry && m.id === made.module_id);
    if (twin) {
      throw expected(`the key ${entry.key} already names module ${made.module_id}, which your files also list as ${twin.c.slug}/${twin.m.key}: remove one of the two entries.`);
    }
    const existed = this.sim.lessons.get(made.module_id);
    entry.id = made.module_id;
    entry.rev = serverModule(made).rev;
    this.moduleNow.set(made.module_id, made);
    if (!existed) {
      append(this.sim.modules.get(courseId)!, made.module_id);
      this.sim.lessons.set(made.module_id, freshOrder());
    }
    this.unsaved.add(course);
    this.settle();
    await this.updateModule(course, entry);
  }

  private async updateModule(course: CourseFolder, entry: ModuleEntry): Promise<void> {
    const now = this.moduleNow.get(entry.id!)!;
    if (moduleContent(entry).title === moduleContent(now).title) return;
    const answer = await this.remote.updateModule(course.id!, entry.id!, { title: entry.title });
    if (moduleContent(answer).title !== moduleContent(entry).title) entry.title = answer.title;
    entry.rev = serverModule(answer).rev;
    this.moduleNow.set(answer.module_id, answer);
    this.save.course(course);
  }

  private async createLesson(course: CourseFolder, module: ModuleEntry, lesson: LessonFile): Promise<void> {
    const courseId = course.id!;
    const roomId = fileLesson(this.files.rooms, lesson).room_id;
    if (roomId?.startsWith(PENDING_ROOM)) throw new Error(`createLesson: room ${lesson.room} has no id yet`);
    const made = await this.remote.createLesson(courseId, {
      module_id: module.id!,
      title: lesson.title,
      ...(lesson.video_url === undefined ? {} : { video_url: lesson.video_url }),
      body: lesson.body,
      links: lesson.links,
      ...(roomId === null ? {} : { room_id: roomId }),
      requires_paid_plan: lesson.requires_paid_plan,
      published: lesson.published,
      client_id: await createClientId(this.communityId, 'lesson', `${course.slug}/${lesson.slug}`),
    });
    if (made.course_id !== courseId) {
      throw expected(
        `the file name ${course.slug}/${lesson.slug} already names lesson ${show(made.title)} (${made.lesson_id}) of ${courseName(this.files, this.plan.server, made.course_id)}: ` +
          "a lesson's file name keeps naming its lesson, and a lesson does not move between courses. Give the new lesson another file name.",
      );
    }
    const twin = this.lessons().find((other) => other.lesson !== lesson && other.lesson.id === made.lesson_id);
    if (twin) {
      throw expected(`the file name ${lesson.slug} already names lesson ${made.lesson_id}, which your files also list as ${twin.course.slug}/${twin.lesson.slug}: remove one of the two.`);
    }
    const existed = this.sim.at.has(made.lesson_id);
    lesson.id = made.lesson_id;
    lesson.rev = serverLesson(made).rev;
    this.lessonNow.set(made.lesson_id, made);
    if (!existed) {
      append(this.sim.lessons.get(made.module_id)!, made.lesson_id);
      this.sim.at.set(made.lesson_id, made.module_id);
    }
    this.save.lesson(course, lesson);
    this.settle();
    // Reconcile: the answer may be a lesson made by an earlier attempt, and changed since. A
    // different module is a place, which the places phase writes.
    await this.updateLesson(course, lesson, false);
  }

  private async updateLesson(course: CourseFolder, lesson: LessonFile, waitForRooms: boolean): Promise<void> {
    const now = this.lessonNow.get(lesson.id!)!;
    const body = lessonPatch(this.files.rooms, lesson, now, waitForRooms);
    if (!body) return;
    const answer = await this.remote.updateLesson(course.id!, lesson.id!, body);
    takeLesson(this.files.rooms, lesson, answer, new Set(Object.keys(body)));
    lesson.rev = serverLesson(answer).rev;
    this.lessonNow.set(answer.lesson_id, answer);
    this.save.lesson(course, lesson);
  }

  /**
   * A position write. One whose answer is lost may have moved the child or not: the server is read
   * again, and when the child stands where the write put it, the write is recorded as if answered —
   * the orders it changed, and no other — before the failure stands. A refusal is not read back.
   */
  private async place(move: PlaceMove): Promise<void> {
    try {
      await this.sendPlace(move);
    } catch (err) {
      if (outcomeUnknown(err)) await this.recordIfLanded(move).catch(() => undefined);
      throw err;
    }
  }

  private async sendPlace(move: PlaceMove): Promise<void> {
    if (move.kind === 'course') {
      const answer = await this.remote.updateCourse(move.id, { position: move.position });
      this.placedCourse(answer, answer.position);
    } else if (move.kind === 'module') {
      const answer = await this.remote.updateModule(move.courseId, move.id, { position: move.position });
      this.placedModule(move.courseId, answer, answer.position);
    } else {
      const from = this.sim.at.get(move.id)!;
      const body = from === move.moduleId ? { position: move.position } : { module_id: move.moduleId, position: move.position };
      const answer = await this.remote.updateLesson(move.courseId, move.id, body);
      this.placedLesson(answer, answer.module_id, answer.position);
    }
  }

  /** A position write whose answer was lost, recorded when a read finds its child where it put it. */
  private async recordIfLanded(move: PlaceMove): Promise<void> {
    const wanted = (order: OrderSim) => {
      const ids = [...order.server];
      placeAt(ids, move.id, move.position);
      return ids.indexOf(move.id);
    };
    if (move.kind === 'course') {
      const courses = await this.remote.listCourses();
      const at = courses.findIndex((course) => course.course_id === move.id);
      if (at >= 0 && at === wanted(this.sim.courses)) this.placedCourse(courses[at], at);
      return;
    }
    const course = await this.remote.exportCourse(move.courseId);
    if (move.kind === 'module') {
      const at = course.modules.findIndex((module) => module.module_id === move.id);
      if (at >= 0 && at === wanted(this.sim.modules.get(move.courseId)!)) this.placedModule(move.courseId, course.modules[at], at);
      return;
    }
    const lessons = course.modules.find((module) => module.module_id === move.moduleId)?.lessons ?? [];
    const at = lessons.findIndex((lesson) => lesson.lesson_id === move.id);
    if (at >= 0 && at === wanted(this.sim.lessons.get(move.moduleId)!)) this.placedLesson(lessons[at], move.moduleId, at);
  }

  private placedCourse(now: CommunityCourseDraft, position: number): void {
    shift(this.sim.courses, now.course_id, position);
    this.courseNow.set(now.course_id, now);
    this.settle();
  }

  private placedModule(courseId: string, now: CommunityModuleDraft, position: number): void {
    shift(this.sim.modules.get(courseId)!, now.module_id, position);
    this.moduleNow.set(now.module_id, now);
    this.settle();
  }

  private placedLesson(now: ServerLesson, moduleId: string, position: number): void {
    const from = this.sim.at.get(now.lesson_id)!;
    if (moduleId !== from) leave(this.sim.lessons.get(from)!, now.lesson_id);
    shift(this.sim.lessons.get(moduleId)!, now.lesson_id, position);
    this.sim.at.set(now.lesson_id, moduleId);
    this.lessonNow.set(now.lesson_id, now);
    this.settle();
  }

  /**
   * After a write that changed a parent's order (or a refresh), each such parent's synced list is
   * recorded and the files keeping a changed one are saved. A synced list is the server's order as
   * its own placement leaves it — less a lesson the server moved in since, and with one it moved
   * out, which the files have not taken: their base stays the module the files last synced them in
   * — or, for a parent whose order changed on the server only, the files' synced list as push left
   * it (`OrderSim.pinned`).
   */
  private settle(): void {
    const record = (order: OrderSim | undefined, current: string[] | undefined, base: (order: OrderSim, current: readonly string[]) => string[], set: (ids: string[]) => void, owner: CourseFolder | null) => {
      if (!order?.touched || order.conflict) return;
      order.touched = false;
      const ids = order.pinned ?? base(order, current ?? []);
      if (current && sameOrder(current, ids)) return;
      set([...ids]);
      this.unsaved.add(owner);
    };
    const asIs = (order: OrderSim) => order.server;
    record(this.sim.courses, this.files.synced_courses, asIs, (ids) => { this.files.synced_courses = ids; }, null);
    for (const course of this.files.courses) {
      const modules = course.id === undefined ? undefined : this.sim.modules.get(course.id);
      if (!modules) continue;
      const synced = (): CourseSynced => (course.synced ??= { lessons: {} });
      record(modules, course.synced?.modules, asIs, (ids) => { synced().modules = ids; }, course);
      for (const moduleId of modules.server) {
        record(this.sim.lessons.get(moduleId), course.synced?.lessons[moduleId], (order, current) => this.lessonsBase(moduleId, order.server, current), (ids) => { synced().lessons[moduleId] = ids; }, course);
      }
    }
    this.flush();
  }

  /**
   * A module's synced lessons: the server's, less those it moved in and with those it moved out, the
   * files not having taken either — each one moved out back in its place in the synced list as it
   * stood (`current`).
   */
  private lessonsBase(moduleId: string, server: readonly string[], current: readonly string[]): string[] {
    const movedIn = this.movedIn.get(moduleId);
    const ids = server.filter((id) => !movedIn?.has(id));
    for (const id of this.movedOut.get(moduleId) ?? []) if (!ids.includes(id)) insertAfter(ids, id, current);
    return ids;
  }
}

// ── Pull ──

/** A lesson file holding the server's lesson. */
function lessonFromServer(rooms: RoomEntry[], lesson: ExportLesson, slug: string): LessonFile {
  const room = roomKeyOf(rooms, lesson.room_id);
  return {
    slug,
    id: lesson.lesson_id,
    rev: serverLesson(lesson).rev,
    title: lesson.title,
    published: lesson.published,
    ...(room === undefined ? {} : { room }),
    ...(lesson.video ? { video_url: communityLessonVideoWatchUrl(lesson.video) } : {}),
    links: lesson.links.map((link) => ({ title: link.title, url: link.url })),
    requires_paid_plan: lesson.requires_paid_plan,
    body: toLf(lesson.body),
  };
}

/** A module entry holding the server's module, with no lessons yet. */
function moduleFromServer(module: ServerModule, key: string): ModuleEntry {
  return { key, id: module.module_id, rev: serverModule(module).rev, title: module.title, lessons: [] };
}

/** The server's orders of a course, as a synced block records them. */
function syncedFromServer(course: CommunityCourseExport): CourseSynced {
  return {
    modules: course.modules.map((module) => module.module_id),
    lessons: Object.fromEntries(course.modules.map((module) => [module.module_id, module.lessons.map((lesson) => lesson.lesson_id)])),
  };
}

/** A course folder holding the server's course, its modules and lessons named from their titles. */
function courseFromServer(rooms: RoomEntry[], course: CommunityCourseExport, slug: string): CourseFolder {
  const keys = new Set<string>();
  const slugs = new Set<string>();
  return {
    slug,
    id: course.course.course_id,
    rev: serverCourse(course.course).rev,
    title: course.course.title,
    description: course.course.description,
    published: course.course.published,
    modules: course.modules.map((module) => {
      const key = localName(module.title, module.module_id, keys, 'module');
      keys.add(key);
      const entry = moduleFromServer(module, key);
      entry.lessons = module.lessons.map((lesson) => {
        const lessonSlug = localName(lesson.title, lesson.lesson_id, slugs, 'lesson');
        slugs.add(lessonSlug);
        return lessonFromServer(rooms, lesson, lessonSlug);
      });
      return entry;
    }),
    synced: syncedFromServer(course),
    unlisted: [],
  };
}

/** A first pull's courses: every one, in the server's order. */
export function coursesFromServer(files: CommunityFiles, server: ServerCourses): void {
  const slugs = new Set<string>();
  files.courses = server.courses.map((course) => {
    const slug = localName(course.course.title, course.course.course_id, slugs, 'course');
    slugs.add(slug);
    return courseFromServer(files.rooms, course, slug);
  });
  files.synced_courses = server.ids;
}

/**
 * `entries` rebuilt from `placed` — the entries the server's order sets, in that order — with each
 * other entry (a create; a lesson the files keep here against the server) after the entry it
 * followed in `entries`, or first.
 */
function takeOrder<E>(entries: readonly E[], placed: readonly E[]): E[] {
  const out = [...placed];
  for (const entry of entries) if (!out.includes(entry)) insertAfter(out, entry, entries);
  return out;
}

/** `entries` in their own order, with each of `arrivals` (by id) after the entry it follows on the server, or first (`mergeOrder`). */
function mergeArrivals<E extends { id?: string }>(entries: readonly E[], server: readonly string[], arrivals: ReadonlyMap<string, E>): E[] {
  const named = new Set(server.filter((id) => !arrivals.has(id)));
  return mergeOrder(listed(entries), server, named).flatMap((slot) => ('entry' in slot ? [slot.entry] : arrivals.get(slot.untracked) ?? []));
}

/** Whether pull takes the server's side of an entity (`remote`, or a conflict with `--theirs`). */
export type Takes = (state: SyncState) => boolean;
/** Pull's record of what it keeps (`local`) and leaves in conflict. */
export type Note = (state: SyncState, what: string) => void;

/**
 * Apply the pull column of the Sync rules table to the courses: what changed on the server only
 * is taken, what changed in the files only is kept, a conflict is reported (or taken, `--theirs`),
 * and what the server has and the files do not is written in. Each order's synced list then
 * becomes the server's, but where a conflict is left in the files.
 */
export function pullCourses(files: CommunityFiles, plan: CoursesPlan, takes: Takes, note: Note, listing: Listing): void {
  for (const course of plan.courses) pullContent(files, course, takes, note, listing);

  const tracked = new Map(files.courses.flatMap((course) => (course.id === undefined ? [] : [[course.id, course] as const])));
  const held = new Set(files.unlistedCourses.flatMap((folder) => (folder.id === null ? [] : [folder.id])));
  // Compared case-insensitively: on a case-insensitive filesystem `Extras/` is `extras/`.
  const slugs = new Set([...files.courses.map((course) => course.slug), ...files.unlistedCourses.map((folder) => folder.name)].map((name) => name.toLowerCase()));
  const courseFor = (id: string): CourseFolder | null => {
    const known = tracked.get(id);
    if (known) return known;
    const course = plan.server.course.get(id)!;
    if (held.has(id)) {
      listing.kept.push(`course ${show(course.course.title)} (${id}): its folder is not listed in ${COMMUNITY_FILE} courses — list it to pull it`);
      return null;
    }
    const slug = localName(course.course.title, id, slugs, 'course');
    slugs.add(slug);
    listing.changes.push(`+ course ${slug}: ${show(course.course.title)}`);
    return courseFromServer(files.rooms, course, slug);
  };
  const order = plan.order;
  if (takes(order.state)) {
    files.courses = takeOrder(files.courses, order.server.flatMap((id) => courseFor(id) ?? []));
    listing.changes.push('~ courses order');
  } else {
    const arrivals = new Map(order.server.filter((id) => !tracked.has(id)).flatMap((id) => {
      const course = courseFor(id);
      return course ? [[id, course] as const] : [];
    }));
    files.courses = mergeArrivals(files.courses, order.server, arrivals);
    note(order.state, 'courses order');
  }
  if (synced(order.state, takes)) files.synced_courses = [...order.server];
  for (const course of plan.courses) if (course.export) pullTree(files, plan.server, course, takes, note, listing);
}

/** Whether pull records a parent's synced list once its list is merged: on every verdict but a conflict it leaves. */
const synced = (state: SyncState, takes: Takes) => state !== 'conflict' || takes(state);

/** The course's, its modules' and its lessons' own content, per entity. */
function pullContent(files: CommunityFiles, course: CoursePlan, takes: Takes, note: Note, listing: Listing): void {
  const { folder } = course;
  if (course.server) {
    const { state, server, serverRev } = course.server;
    if (takes(state)) {
      listing.changes.push(`~ course ${folder.slug}: ${courseChanges(courseContent(folder), courseContent(server)).join('; ')}`);
      takeCourse(folder, server);
    }
    if (takes(state) || state === 'converged') folder.rev = serverRev;
    note(state, `course ${folder.slug}`);
  } else if (folder.id === undefined) {
    listing.kept.push(`course ${folder.slug} (to create)`);
  }
  for (const module of course.modules) {
    const at = `module ${folder.slug}/${module.entry.key}`;
    if (!module.server) {
      if (module.entry.id === undefined) listing.kept.push(`${at} (to create)`);
      continue;
    }
    const { state, server, serverRev } = module.server;
    if (takes(state)) {
      listing.changes.push(`~ ${at}: title ${show(module.entry.title)} → ${show(server.title)}`);
      if (moduleContent(module.entry).title !== moduleContent(server).title) module.entry.title = server.title;
    }
    if (takes(state) || state === 'converged') module.entry.rev = serverRev;
    note(state, at);
  }
  for (const lesson of course.lessons) {
    const at = `lesson ${folder.slug}/${lesson.lesson.slug}`;
    if (!lesson.server) {
      if (lesson.lesson.id === undefined) listing.kept.push(`${at} (to create)`);
      continue;
    }
    const { state, server, serverRev } = lesson.server;
    if (takes(state)) {
      const changes = lessonChanges(fileLesson(files.rooms, lesson.lesson), lessonContent(server), (id) => roomName(files.rooms, id));
      listing.changes.push(`~ ${at}: ${changes.join('; ')}`);
      takeLesson(files.rooms, lesson.lesson, server);
    }
    if (takes(state) || state === 'converged') lesson.lesson.rev = serverRev;
    note(state, at);
  }
}

/**
 * A tracked course's modules and lessons, in order. Each lesson first gets its module in the files
 * — the server's, where pull takes its place's change (or its conflict, `--theirs`); the files'
 * otherwise — and each server lesson the files name nowhere its server module. Each module's list
 * is then those lessons: in the server's order where pull takes the module's order, with the ones
 * the files keep here (and creates) after the lesson they followed; in the files' order otherwise,
 * with the ones arriving after the lesson they follow on the server. Each synced list becomes the
 * server's but where a conflict is left in the files: a module's order conflict keeps its list, and
 * a lesson's place conflict keeps it out of the server module's and in its base module's.
 */
function pullTree(files: CommunityFiles, index: ServerCourses, course: CoursePlan, takes: Takes, note: Note, listing: Listing): void {
  const { folder } = course;
  const server = course.export!;
  const keys = new Set(folder.modules.map((module) => module.key));
  const unlisted = folder.unlisted.map((file) => ({ slug: path.basename(file.name, LESSON_FILE_EXT), id: file.id }));
  const slugs = new Set([...folder.modules.flatMap((module) => module.lessons.map((lesson) => lesson.slug)), ...unlisted.map((file) => file.slug)].map((name) => name.toLowerCase()));
  const held = new Set(unlisted.flatMap((file) => (file.id === null ? [] : [file.id])));

  // Modules: an entry for each server module the files do not have, then the course's list.
  const fresh = new Set<ModuleEntry>();
  const moduleFor = (id: string): ModuleEntry | null => {
    const known = folder.modules.find((module) => module.id === id);
    if (known) return known;
    const { module } = index.module.get(id)!;
    const key = localName(module.title, id, keys, 'module');
    keys.add(key);
    listing.changes.push(`+ module ${folder.slug}/${key}: ${show(module.title)}`);
    const entry = moduleFromServer(module, key);
    fresh.add(entry);
    return entry;
  };
  const modulesOrder = course.modulesOrder!;
  const what = `modules order of course ${folder.slug}`;
  if (takes(modulesOrder.state)) {
    folder.modules = takeOrder(folder.modules, modulesOrder.server.flatMap((id) => moduleFor(id) ?? []));
    listing.changes.push(`~ ${what}`);
  } else {
    const arrivals = new Map(modulesOrder.server.filter((id) => !folder.modules.some((m) => m.id === id)).flatMap((id) => {
      const entry = moduleFor(id);
      return entry ? [[id, entry] as const] : [];
    }));
    folder.modules = mergeArrivals(folder.modules, modulesOrder.server, arrivals);
    note(modulesOrder.state, what);
  }
  const entryOf = (moduleId: string) => folder.modules.find((module) => module.id === moduleId);

  // Each lesson's module in the files.
  const home = new Map<LessonFile, ModuleEntry>();
  const keptApart = new Set<LessonPlan>();
  for (const lesson of course.lessons) {
    const { placement } = lesson;
    if (!placement) {
      home.set(lesson.lesson, lesson.module);
      continue;
    }
    const at = `lesson ${folder.slug}/${lesson.lesson.slug}`;
    if (placement.state === 'conflict') note('conflict', `${at}: moved in the files and on the server`);
    if (placement.state === 'local') listing.kept.push(`${at}: moved to module ${lesson.module.key}`);
    const taken = takes(placement.state) && placement.state !== 'converged';
    const to = taken ? entryOf(placement.server) : undefined;
    if (to && to !== lesson.module) listing.changes.push(`~ ${at}: moved to module ${to.key}`);
    if (placement.state === 'conflict' && !taken) keptApart.add(lesson);
    home.set(lesson.lesson, to ?? lesson.module);
  }
  // The server's lessons the files name nowhere, in their server module.
  const named = idsOf(course.lessons.map((lesson) => lesson.lesson));
  for (const module of server.modules) {
    const entry = entryOf(module.module_id);
    if (!entry) continue;
    for (const lesson of module.lessons) {
      if (named.has(lesson.lesson_id)) continue;
      if (held.has(lesson.lesson_id)) {
        listing.kept.push(`lesson ${show(lesson.title)} (${lesson.lesson_id}) of course ${folder.slug}: its file is listed by no module — list it to pull it`);
        continue;
      }
      const slug = localName(lesson.title, lesson.lesson_id, slugs, 'lesson');
      slugs.add(slug);
      listing.changes.push(`+ lesson ${folder.slug}/${slug}: ${show(lesson.title)}`);
      home.set(lessonFromServer(files.rooms, lesson, slug), entry);
    }
  }

  // Each module's list.
  for (const entry of folder.modules) {
    const members = [...home].filter(([, at]) => at === entry).map(([lesson]) => lesson);
    const own = entry.lessons.filter((lesson) => lesson.id === undefined || members.includes(lesson));
    const serverIds = entry.id === undefined ? [] : index.module.get(entry.id)?.module.lessons.map((lesson) => lesson.lesson_id) ?? [];
    const plan = course.modules.find((m) => m.entry === entry);
    const take = fresh.has(entry) || (plan?.lessonsOrder !== null && plan?.lessonsOrder !== undefined && takes(plan.lessonsOrder.state));
    if (take) {
      const byId = new Map(members.flatMap((lesson) => (lesson.id === undefined ? [] : [[lesson.id, lesson] as const])));
      entry.lessons = takeOrder(own, serverIds.flatMap((id) => byId.get(id) ?? []));
      if (!fresh.has(entry)) listing.changes.push(`~ lessons order of module ${folder.slug}/${entry.key}`);
    } else {
      const arrivals = new Map(members.filter((lesson) => !own.includes(lesson)).flatMap((lesson) => (lesson.id === undefined ? [] : [[lesson.id, lesson] as const])));
      entry.lessons = mergeArrivals(own, serverIds, arrivals);
      if (plan?.lessonsOrder) note(plan.lessonsOrder.state, `lessons order of module ${folder.slug}/${entry.key}`);
    }
  }

  // The synced lists.
  const was = folder.synced;
  const lessons: Record<string, string[]> = {};
  for (const module of server.modules) {
    const verdict = course.modules.find((m) => m.entry.id === module.module_id)?.lessonsOrder;
    if (verdict && !synced(verdict.state, takes) && was?.lessons[module.module_id]) {
      lessons[module.module_id] = was.lessons[module.module_id];
      continue;
    }
    const kept = [...keptApart].filter((lesson) => lesson.placement!.server === module.module_id).map((lesson) => lesson.lesson.id!);
    const based = [...keptApart].filter((lesson) => lesson.placement!.base === module.module_id).map((lesson) => lesson.lesson.id!);
    const ids = module.lessons.map((lesson) => lesson.lesson_id).filter((id) => !kept.includes(id));
    for (const id of based) if (!ids.includes(id)) insertAfter(ids, id, was?.lessons[module.module_id] ?? []); // back in its place in the base
    lessons[module.module_id] = ids;
  }
  folder.synced = {
    modules: synced(modulesOrder.state, takes) || !was?.modules ? server.modules.map((module) => module.module_id) : was.modules,
    lessons,
  };
}

// ── Two reads of one community ──

/** The courses, modules and lessons whose server state differs between two reads, by name. */
export function coursesChangedSince(before: ServerCourses, after: ServerCourses): string[] {
  const states = (server: ServerCourses) => {
    const out = new Map<string, { label: string; rev: string }>();
    out.set('courses', { label: 'courses order', rev: server.ids.join() });
    for (const course of server.courses) {
      const courseId = course.course.course_id;
      const label = `course ${show(course.course.title)}`;
      out.set(courseId, { label, rev: serverCourse(course.course).rev });
      out.set(`modules:${courseId}`, { label, rev: course.modules.map((m) => m.module_id).join() });
      for (const module of course.modules) {
        const moduleLabel = `module ${show(module.title)}`;
        out.set(module.module_id, { label: moduleLabel, rev: serverModule(module).rev });
        out.set(`lessons:${module.module_id}`, { label: moduleLabel, rev: module.lessons.map((l) => l.lesson_id).join() });
        for (const lesson of module.lessons) out.set(lesson.lesson_id, { label: `lesson ${show(lesson.title)}`, rev: serverLesson(lesson).rev });
      }
    }
    return out;
  };
  const was = states(before);
  const changed = new Set<string>();
  for (const [key, now] of states(after)) if (was.get(key)?.rev !== now.rev) changed.add(now.label);
  return [...changed];
}
